import { useReducer, useEffect, useRef, DependencyList } from 'react'
import toast from 'react-hot-toast'
import {
	addDoc,
	collection,
	deleteDoc,
	doc,
	DocumentData,
	documentId,
	DocumentReference,
	DocumentSnapshot,
	getDoc,
	getDocs,
	limit,
	onSnapshot,
	orderBy,
	Query,
	query,
	QueryDocumentSnapshot,
	queryEqual,
	QuerySnapshot,
	refEqual,
	serverTimestamp,
	setDoc,
	startAfter,
	updateDoc,
	where
} from 'firebase/firestore'

import { firestoreClient } from '../lib/firebase'
import { checkScratchPaymentMethods } from '../constants'

const firestore = firestoreClient()

const COLLECTION_WEB_PAYMENT_PRACTICES = 'webPaymentPractices'
const COLLECTION_NAME_CUSTOMERS = 'stripeCustomers'
const COLLECTION_NAME_DISPUTES = 'stripeDisputes'

const reducer = (state, action) => {
	switch (action.type) {
		case 'idle':
			return { status: 'idle', data: undefined, error: undefined }
		case 'loading':
			return { status: 'loading', data: undefined, error: undefined }
		case 'success':
			return { status: 'success', data: action.payload, error: undefined }
		case 'error':
			toast.error('Data Loading Issue.  You may need to refresh the page.')
			console.error(JSON.stringify(action.payload))
			return { status: 'error', data: undefined, error: action.payload }
		default:
			throw new Error('invalid action')
	}
}

// Store an old value and return the old value if it's the same to prevent requerying.
function useMemoCompare<T>(
	next: T,
	compare: (left: T | undefined, right: T | undefined) => boolean
) {
	const previousRef = useRef<T>()
	const previous = previousRef.current
	const isEqual = compare(previous, next)
	useEffect(() => {
		if (!isEqual) {
			previousRef.current = next
		}
	})
	return isEqual ? previous : next
}

// Get doc data and merge doc.id => get array of doc data => create useQuery hook
function getDocData(d: any) {
	return d.exists() === true ? { id: d.id, ...d.data() } : null
}
function getCollectionData<T>(c: QuerySnapshot<T>) {
	return c.docs.map(getDocData)
}

// Internal function to memoize the queries and return state.
function memoizeQuery<T>(q: Query<T> | DocumentReference<T> | null, isQuery: boolean) {
	const initialState = {
		status: q ? 'loading' : 'idle',
		data: undefined,
		error: undefined
	}
	const [state, dispatch] = useReducer(reducer, initialState)
	const queryCached = isQuery
		? useMemoCompare<Query>(q as Query, (prevQuery, right) =>
				prevQuery && right ? queryEqual(right, prevQuery) : false
			)
		: useMemoCompare<DocumentReference>(q as DocumentReference, (prevQuery, right) =>
				prevQuery && right ? refEqual(right, prevQuery) : false
			)
	useEffect(() => {
		if (!queryCached) {
			dispatch({ type: 'idle' })
			return
		}
		dispatch({ type: 'loading' })
		// eslint-disable-next-line consistent-return
		if (isQuery) {
			return onSnapshot(
				queryCached as Query,
				(response) => {
					const data = getCollectionData(response)
					dispatch({ type: 'success', payload: data })
				},
				(error) => {
					dispatch({ type: 'error', payload: error })
				}
			)
		}
		return onSnapshot(
			queryCached as DocumentReference,
			(response) => {
				const data = getDocData(response)
				dispatch({ type: 'success', payload: data })
			},
			(error) => {
				dispatch({ type: 'error', payload: error })
			}
		)
	}, [queryCached, isQuery]) // Only run effect if queryCached changes
	return state
}

// With modularity, have a different version for Doc Ref and Query/Collection ref
function useQuery<T>(q: Query<T> | null) {
	return memoizeQuery(q, true)
}

// With modularity, have a different version for Doc Ref and Query/Collection ref
function useDocQuery<T>(q: DocumentReference<T> | null) {
	return memoizeQuery(q, false)
}

/** MEMOIZE API CALL */
/**
 * Temporary hack to allow API calls to look like firestore onSnapshot calls with memoization.
 *
 * @param api the api call to perform.
 * @returns a state object with status, data, and error, that will be updated with useEffect upon changes in results
 */
export function memoizeApiCall<T>(api: () => Promise<T> | null, deps?: DependencyList) {
	const initialState = {
		status: 'loading',
		data: undefined,
		error: undefined
	}
	const [state, dispatch] = useReducer(reducer, initialState)
	// Next two lines are needed to keep "law of hooks" working, since firebase memoizes the query using useRef/useEffect
	useRef(null)
	useEffect(() => {
		'ignore'
	})

	useEffect((): void => {
		const call = api()
		if (call === null) {
			// If there isn't anything to query, we're idle
			dispatch({ type: 'idle' })
			return
		}
		dispatch({ type: 'loading' })
		call
			.then((result) => {
				if ((result as any)?.error) {
					dispatch({ type: 'error', payload: (result as any)?.error })
				} else if (result) {
					dispatch({ type: 'success', payload: result })
				}
			})
			.catch((error) => {
				dispatch({ type: 'error', payload: error })
			})
		// Ignore the useEffect callback
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, deps ?? []) // Only run the effect once to prevent looping.  Including api here makes errors loop infinitely.
	return state
}

// export function batch() { firestore.batch(); }

/** ** PRACTICES *** */
export function getPracticeDocRef(id: string | undefined) {
	return getDoc(doc(collection(firestore, 'practices'), id))
}
export function updatePractice(cuid: string | undefined, data: IPractice) {
	return updateDoc(doc(collection(firestore, 'practices'), cuid), data as any)
}

/** ** USERS *** */
export function useUser(uid: string | undefined) {
	return useDocQuery(uid ? doc(collection(firestore, 'users'), uid) : null)
}

export const getUser = (uid: string | undefined) => getDoc(doc(collection(firestore, 'users'), uid))

/**
 * @deprecated use /apis/user/updateUser
 */
export function updateUser(uid: string | undefined, data: any) {
	return updateDoc(doc(collection(firestore, 'users'), uid), data).then()
}

/** ** ITEMS / CHARGES / INVOICES / ETC *** */

export function useAllDisputes() {
	return useQuery(query(collection(firestore, 'disputesTest2'), orderBy('created', 'desc')))
}

// TODO [DASH-8441]: Remove once consolidated practice is GA — practice reads move to GQL.
export function useAllPractices(skip = false) {
	return useQuery(skip ? null : collection(firestore, 'practices'))
}

// TODO [DASH-8441]: Remove once consolidated practice is GA — practice reads move to GQL.
export function useAllPracticesByConsolidatorId(consolidatorId: string | undefined) {
	return useQuery(
		consolidatorId
			? query(collection(firestore, 'practices'), where('consolidatorId', '==', consolidatorId))
			: null
	)
}

export async function useUserPractices(practices: string[]) {
	const ref = await query(
		collection(firestore, 'practices'),
		where('cuid', 'in', practices),
		orderBy('clinicName')
	)
	const practiceDocs = await getDocs(ref)
	return practiceDocs.docs.map((d) => d.data())
}

export function useChargesByClinic(acctID, startDate: number, endDate: number) {
	const response = useQuery(
		acctID
			? query(
					collection(firestore, 'stripeCharges'),
					where('transfer_data.destination', '==', acctID), // ownerID
					where('created', '>=', startDate),
					where('created', '<=', endDate),
					orderBy('created', 'desc')
				)
			: null
	)
	return response
}

export function useCheckAttemptsByClinic(
	acctID: string | undefined,
	startDate: number,
	endDate: number
) {
	const response = useQuery(
		acctID
			? query(
					collection(firestore, 'stripeCharges'),
					where('transfer_data.destination', '==', acctID),
					where('payment_method_details.type', '==', 'us_bank_account'),
					where('created', '>=', startDate),
					where('created', '<=', endDate),
					orderBy('created', 'desc'),
					orderBy('id', 'desc')
				)
			: null
	)
	return response
}

export function useCheckAttemptsByConsolidator(
	consolidatorId: string | undefined,
	startDate: number,
	endDate: number
) {
	const response = useQuery(
		consolidatorId
			? query(
					collection(firestore, 'stripeCharges'),
					where('payment_method_details.type', '==', 'us_bank_account'),
					where('metadata.consolidatorId', '==', consolidatorId),
					where('created', '>=', startDate),
					where('created', '<=', endDate),
					orderBy('created', 'desc'),
					orderBy('id', 'desc')
				)
			: null
	)
	return response
}

export function useLockboxChecksByConsolidator(consolidatorId: string) {
	const response = useQuery(
		consolidatorId
			? query(
					collection(firestore, 'stripeCharges'),
					where('payment_method_details.type', '==', 'us_bank_account'),
					where('metadata.consolidatorId', '==', consolidatorId),
					orderBy('created', 'desc'),
					orderBy('metadata.lockboxInvoiceId', 'asc')
				)
			: null
	)
	return response
}

export function useChargesByClinicDataGrid(acctID: string | undefined) {
	const response = useQuery(
		acctID
			? query(
					collection(firestore, 'stripeCharges'),
					where('transfer_data.destination', '==', acctID), // ownerID
					orderBy('created', 'desc')
				)
			: null
	)
	return response
}

export function useChargesByCustomer(acctID?: string, customerID?: string) {
	return useQuery(
		acctID
			? query(
					collection(firestore, 'stripeCharges'),
					where('transfer_data.destination', '==', acctID), // ownerID
					where('customer', '==', customerID), // customerID
					orderBy('created', 'desc')
				)
			: null
	)
}

export function usePayoutsTest() {
	return useQuery(collection(firestore, 'payoutsTest'))
}

export function useReconciliationByClinic(
	acctID: string | undefined,
	startDate: number,
	endDate: number
) {
	return useQuery(
		acctID
			? query(
					collection(firestore, 'stripeCharges'),
					where('transfer_data.destination', '==', acctID), // ownerID
					where('paid', '==', true),
					where('refunded', '==', false),
					where('created', '>=', startDate),
					where('created', '<=', endDate),
					orderBy('created', 'desc')
				)
			: null
	)
}

export function useRefundsByClinic(acctID: string | undefined) {
	return useQuery(
		acctID
			? query(
					collection(firestore, 'stripeCharges'),
					where('transfer_data.destination', '==', acctID), // ownerID
					where('refunds.total_count', '!=', 0),
					orderBy('refunds.total_count', 'desc'),
					orderBy('created', 'desc')
				)
			: null
	)
}

export function useCheckInvoicesByClinic(acctID: string | undefined) {
	return useQuery(
		acctID
			? query(
					collection(firestore, 'stripeInvoices'),
					where('transfer_data.destination', '==', acctID),
					where('metadata.scratchPaymentMethod', '==', 'check_payment'),
					orderBy('created', 'desc')
				)
			: null
	)
}

export function useCheckInvoicesByConsolidator(
	consolidatorId: string | number | undefined,
	start: number,
	end: number
) {
	return useQuery(
		consolidatorId
			? query(
					collection(firestore, 'stripeInvoices'),
					where('metadata.scratchPaymentMethod', '==', 'check_payment'),
					where('metadata.consolidatorId', '==', consolidatorId),
					where('created', '>=', start),
					where('created', '<=', end),
					orderBy('created', 'desc')
				)
			: null
	)
}

export function useCheckPaymentIntentsByConsolidatorId(
	consolidatorId: string | undefined,
	start: number,
	end: number
) {
	return useQuery(
		consolidatorId
			? query(
					collection(firestore, 'paymentIntents'),
					where('metadata.consolidatorId', '==', consolidatorId),
					where('metadata.scratchPaymentMethod', 'in', checkScratchPaymentMethods),
					where('created', '>=', start),
					where('created', '<=', end),
					orderBy('created', 'desc')
				)
			: null
	)
}

export function useManageClinics(consolidatorId: string | undefined) {
	return useQuery(
		consolidatorId
			? query(
					collection(firestore, 'practices'),
					where('consolidatorId', '==', consolidatorId),
					orderBy('clinicName')
				)
			: null
	)
}

export function useManageNotifications(stripeAccountId: string | undefined) {
	return useQuery(
		stripeAccountId
			? query(
					collection(firestore, 'actionNeeded'),
					where('destination', '==', stripeAccountId),
					where('isComplete', '==', false),
					orderBy('created', 'desc')
				)
			: null
	)
}

export function usePracticeInfo(currentPractice: string | undefined) {
	return useQuery(
		currentPractice
			? query(
					collection(firestore, 'practices'),
					where('stripeAccountId', '==', currentPractice)
					// orderBy('created', 'desc'))
				)
			: null
	)
}

// TO DO: Add consolidatorId to the Stripe Metadata, and then add that as a filter here.
export function useStripeAccounts(consolidatorId: string | undefined) {
	return useQuery(
		query(
			collection(firestore, 'stripeAccounts'),
			where('metadata.consolidatorId', '==', consolidatorId),
			orderBy('id', 'desc')
		)
	)
}

export async function updateCharge(id: string | undefined, data: any) {
	const chargeRef = doc(collection(firestore, 'stripeCharges'), id)
	const res = updateDoc(chargeRef, data)
	return res
}

export function createCanadaInstallmentPlan(data: any) {
	return addDoc(collection(firestore, 'canadaInstallmentPlans'), {
		...data,
		createdAt: serverTimestamp()
	})
}

export function usePimsClientByClinic(acctID) {
	return useQuery(
		acctID ? query(collection(firestore, 'pimsClient'), where('ClinicID', '==', acctID)) : null
	)
}

export function usePimsClientByClinicBatched(acctID, pageSize, lastDoc) {
	return getDocs(
		query(
			collection(firestore, 'pimsClient'),
			orderBy('ClientCell', 'asc'),
			where('ClinicID', '==', acctID),
			startAfter(lastDoc),
			limit(pageSize)
		)
	)
}

export function usePimsClient(clientId, acctID) {
	return useDocQuery(
		clientId && acctID ? doc(collection(firestore, 'pimsClient'), `${acctID}-${clientId}`) : null
	)
}

export function usePimsClientByFirstLastPhone(firstName, lastName, phone, acctID) {
	if (!phone) {
		return useQuery(
			firstName && lastName && phone && firestore
				? query(
						collection(firestore, 'pimsClient'),
						where('ClinicID', '==', acctID),
						where('ClientFirst', '==', firstName),
						where('ClientLast', '==', lastName)
					)
				: null
		)
	}
	const phoneArea = phone.substring(2, 5)
	const phoneNumPartOne = phone.substring(5, 8)
	const phoneNumPartTwo = phone.substring(8)
	return useQuery(
		firstName && lastName && phone
			? query(
					collection(firestore, 'pimsClient'),
					where('ClinicID', '==', acctID),
					where('ClientFirst', '==', firstName),
					where('ClientLast', '==', lastName),
					where('ClientArea', '==', phoneArea),
					where('ClientPhone', '==', `${phoneNumPartOne}-${phoneNumPartTwo}`)
				)
			: null
	)
}

export function usePimsAnimal(animalId, acctID) {
	return useDocQuery(
		animalId && acctID ? doc(collection(firestore, 'pimsAnimal'), `${acctID}-${animalId}`) : null
	)
}

export function usePimsAppointmentByClinic(acctID) {
	return useQuery(
		acctID
			? query(
					collection(firestore, 'pimsAppointment'),
					where('ClinicID', '==', acctID),
					orderBy('AppointmentDate', 'asc'),
					orderBy('AppointmentTime', 'asc')
				)
			: null
	)
}

export function usePimsAppointmentByParent(animalId, acctID) {
	return useQuery(
		animalId && acctID
			? query(
					collection(firestore, 'pimsAppointment'),
					where('ClinicID', '==', acctID),
					where('AppointmentParent', '==', animalId),
					orderBy('AppointmentDate', 'asc'),
					orderBy('AppointmentTime', 'asc')
				)
			: null
	)
}

export function usePimsAnimalByNameClientClinic(animalName, clientId, acctId) {
	return useQuery(
		animalName && clientId && acctId
			? query(
					collection(firestore, 'pimsAnimal'),
					where('ClinicID', '==', acctId),
					where('AnimalName', '==', animalName),
					where('AnimalClient', '==', clientId)
				)
			: null
	)
}

export function useCustomersByMetadata(acctID) {
	return useQuery(
		acctID
			? query(
					collection(firestore, 'stripeCustomers'),
					where('metadata.stripeAccountId', '==', acctID)
				)
			: null
	)
}

export function useUsersPracticesId(practiceId) {
	return useQuery(
		practiceId
			? query(collection(firestore, 'users'), where('practices', 'array-contains', practiceId))
			: null
	)
}

export function useRegionalManagers(consolidatorID) {
	return useQuery(
		consolidatorID
			? query(
					collection(firestore, 'users'),
					where('consolidatorId', '==', consolidatorID),
					where('isRegionalManager', '==', true),
					orderBy('name', 'asc')
				)
			: null
	)
}

export function useStripeCustomersByID(id) {
	return useDocQuery(id && doc(collection(firestore, 'stripeCustomers'), id))
}

export function usePracticeByID(id) {
	return useDocQuery(id && doc(collection(firestore, 'practices'), id))
}

export function usePracticeFormTemplates(id) {
	return useQuery(
		id
			? query(collection(firestore, 'customFormTemplates'), where('stripeAccountId', '==', id))
			: null
	)
}

export function usePracticeSnippets(id) {
	return useQuery(
		id ? query(collection(firestore, 'customSnippets'), where('stripeAccountId', '==', id)) : null
	)
}

export function deleteSnippet(id) {
	return deleteDoc(doc(collection(firestore, 'customSnippets'), id))
}

export function deleteCustomFormTemplate(id) {
	return deleteDoc(doc(collection(firestore, 'customFormTemplates'), id))
}

export function addCustomSnippet(data) {
	const response = addDoc(collection(firestore, 'customSnippets'), data)
	return response
}

export function updateCustomSnippet(id, data) {
	const response = updateDoc(doc(collection(firestore, 'customSnippets'), id), data)
	return response
}

export function useCustomFormTemplate(id) {
	return useDocQuery(id && doc(collection(firestore, 'customFormTemplates'), id))
}

export function updateCustomFormTemplate(id, data) {
	const response = updateDoc(doc(collection(firestore, 'customFormTemplates'), id), data)
	return response
}

export function addCustomFormTitle(data) {
	const response = addDoc(collection(firestore, 'customFormTemplates'), data)
	return response
}

export function addCustomFormResponse(data) {
	return addDoc(collection(firestore, 'customFormResponses'), {
		...data,
		createdAt: serverTimestamp()
	})
}

export function useFormResponseByConvoSid(convoSid) {
	return useQuery(
		convoSid
			? query(
					collection(firestore, 'customFormResponses'),
					where('conversationSid', '==', convoSid)
				)
			: null
	)
}

function addPaymentIntentStatusFilter(q: Query, status: string) {
	switch (status) {
		case 'all':
			return q
		case undefined:
			return q
		case 'pending':
			return query(
				q,
				where('status', 'in', [
					'requires_payment_method',
					'requires_confirmation',
					'requires_action',
					'processing'
				])
			)
		default:
			return query(q, where('status', '==', status))
	}
}

export function usePaymentIntentsByClinic(acctID, startDate, endDate, status) {
	if (!acctID) {
		return useQuery(null)
	}

	let q = query(
		collection(firestore, 'paymentIntents'),
		where('transfer_data.destination', '==', acctID), // ownerID
		where('created', '>=', startDate),
		where('created', '<=', endDate),
		orderBy('created', 'desc')
	)

	q = addPaymentIntentStatusFilter(q, status)

	return useQuery(q)
}

export function useGetTerminalOrdersList() {
	return useQuery(collection(firestore, 'terminalOrders'))
}

export function useGetReadersReportList() {
	return useQuery(collection(firestore, 'readersReport'))
}

export function useGetUploadReadersReportTask() {
	return useDocQuery(doc(collection(firestore, 'workerTasks'), 'readers_report.upload'))
}

const getDocIfExists = (d: QueryDocumentSnapshot | DocumentSnapshot): any =>
	d.exists() ? { id: d.id, ...d.data() } : null

const formatDocs = (response: QuerySnapshot) => response.docs.map(getDocIfExists)

export function getChargesByClientId(clientId, accountID, startDate) {
	return getDocs(
		query(
			collection(firestore, 'stripeCharges'),
			where('payment_method_details.type', '==', 'us_bank_account'),
			where('metadata.clientId', '==', clientId),
			where('transfer_data.destination', '==', accountID),
			where('created', '>=', startDate),
			orderBy('created', 'desc'),
			limit(1)
		)
	)
		.then(formatDocs)
		.then((docs) => (docs ? docs[0] : null))
}

export function updateTemplateFormFields(templateId, updatedFields) {
	return setDoc(
		doc(collection(firestore, 'customFormTemplates'), templateId),
		{ fields: updatedFields },
		{ merge: true }
	)
}

export function useSpecialName() {
	return useQuery(query(collection(firestore, 'specialName'), orderBy('name', 'asc')))
}

export function getFirstWebPaymentPracticeByPracticeId(practiceId) {
	return getDocs(
		query(
			collection(firestore, COLLECTION_WEB_PAYMENT_PRACTICES),
			where('stripe_account_id', '==', practiceId)
		)
	)
		.then(formatDocs)
		.then((docs) => (docs ? docs[0] : null))
}

export function getCustomer(accountID, clientId) {
	return getDocs(
		query(
			collection(firestore, COLLECTION_NAME_CUSTOMERS),
			where('metadata.stripeAccountId', '==', accountID),
			where('metadata.clientId', '==', clientId),
			orderBy('created', 'desc'),
			limit(1)
		)
	)
		.then(formatDocs)
		.then((docs) => (docs ? docs[0] : null))
}

export async function getCustomerWithSavedCard(accountID: string, clientId: string) {
	return getDocs(
		query(
			collection(firestore, COLLECTION_NAME_CUSTOMERS),
			where('metadata.stripeAccountId', '==', accountID),
			where('metadata.clientId', '==', clientId),
			where('metadata.hasSavedCard', '==', 'true'),
			orderBy('created', 'desc'),
			limit(1)
		)
	)
		.then(formatDocs)
		.then((docs) => (docs ? docs[0] : null))
}

export function getAllCustomersWithSavedCard(accountID) {
	return useQuery(
		query(
			collection(firestore, COLLECTION_NAME_CUSTOMERS),
			where('metadata.stripeAccountId', '==', accountID),
			where('metadata.hasSavedCard', '==', 'true'),
			orderBy('name')
		)
	)
}

export function useDisputes(consolidatorId) {
	return useQuery(
		query(
			collection(firestore, COLLECTION_NAME_DISPUTES),
			where('practiceData.consolidatorId', '==', consolidatorId),
			orderBy('evidence_details.due_by', 'desc')
		)
	)
}

export function useDisputesByPractice(practiceId) {
	return useQuery(
		practiceId
			? query(
					collection(firestore, COLLECTION_NAME_DISPUTES),
					where('charge.destination', '==', practiceId),
					orderBy('evidence_details.due_by', 'desc')
				)
			: null
	)
}

// return disputes that needs evidence submission
export function useDisputesByEvidenceNeeded(practiceId) {
	return useQuery(
		practiceId
			? query(
					collection(firestore, COLLECTION_NAME_DISPUTES),
					where('charge.destination', '==', practiceId),
					where('status', 'in', ['needs_response', 'warning_needs_response'])
				)
			: null
	)
}

export async function getPracticeInstance(id) {
	const locationDocRef = doc(collection(firestore, 'locations'), id)
	const locationDoc = await getDoc(locationDocRef)
	if (!locationDoc.exists()) {
		return {}
	}

	const locationData = locationDoc.data()

	let instances: DocumentData[] = []
	if (locationData.status === 'online') {
		const instancesCollectionRef = collection(locationDocRef, 'instances')
		const instancesSnapshot = await getDocs(instancesCollectionRef)
		instances = instancesSnapshot.docs.map((d) => d.data())
	}

	return {
		...locationData,
		connectedInstances: instances
	}
}

export async function getPracticesByConsolidatorID(consolidatorID) {
	return getDocs(
		query(collection(firestore, 'practices'), where('consolidatorId', '==', consolidatorID))
	).then(formatDocs)
}

export async function getPracticesByConsolidatorIDAndClinicPIMSLocationID(
	consolidatorID,
	clinicPIMsLocationID
) {
	return getDocs(
		query(
			collection(firestore, 'practices'),
			where('consolidatorId', '==', consolidatorID),
			where('clinicPIMsLocationID', '==', clinicPIMsLocationID)
		)
	).then(formatDocs)
}

export const getCustomersByIds = (ids) =>
	getDocs(
		query(collection(firestore, COLLECTION_NAME_CUSTOMERS), where(documentId(), 'in', ids))
	).then(formatDocs)

export function useAppointmentRequestByID(practiceId, appointmentRequestId) {
	return useQuery(
		practiceId && appointmentRequestId
			? query(
					collection(firestore, 'appointmentInsertQueue'),
					where('clinicId', '==', practiceId),
					where('appointmentRequestId', '==', appointmentRequestId),
					limit(1)
				)
			: null
	)
}
